import * as React from "react";
import { onError } from "src/common/onError";
import { ModelessFormStore } from "src/components/ModelessForm";
import { ZObjectItem } from "src/types/ZObjectItem";
import { CommonNodeO2 } from "../Obj2Tab/Obj2Nodes";
import { ObjectsModules, createModules } from "./modules";
import { ResourceState } from "./model/resourceState";
import { LegacyPorts, createLegacyPorts } from "./portsAdapter";

/**
 * Вкладка объектов.
 *
 * Только связывание: ни одного `await`, ни одного разбора по типу узла, ни
 * одного обращения к сети. Сборка модулей вынесена в `modules.ts`,
 * совместимость с формами - в `portsAdapter.ts`, вся работа - в модулях.
 */
export class ObjectsStore {
  private readonly modules: ObjectsModules = createModules();

  /** То, чего ждут переиспользуемые формы вкладки. */
  readonly ports: LegacyPorts = createLegacyPorts(this.modules);

  get formStore(): ModelessFormStore {
    return this.modules.formStore;
  }

  // ---- жизненный цикл

  get data(): ResourceState<true> {
    return this.modules.startup.state;
  }

  /** Повторный вход на вкладку: дерево на месте, крутится оверлей. */
  get refreshing(): boolean {
    return this.modules.startup.refreshing;
  }

  init(): Promise<void> {
    return this.modules.startup.run();
  }

  // ---- дерево

  get treeData(): CommonNodeO2[] {
    return this.ports.treeData;
  }

  get expandedKeys(): React.Key[] {
    return this.modules.expansion.keys;
  }

  expand(key: React.Key, expanded: boolean) {
    this.modules.expansion.set(String(key), expanded);
  }

  /** Узлы, чьи дети загружены. Учёт наш, а не дерева, — см. `loadTreeNode`. */
  get loadedKeys(): React.Key[] {
    return this.modules.tree.loadedIds;
  }

  /**
   * Дети узла для антовского `loadData`.
   *
   * При отказе ветвь сворачивается обратно и показывается причина. Оставить её
   * раскрытой нельзя: дерево повторяет загрузку на каждую перерисовку, пока
   * узел раскрыт и не загружен, и после десятка неудач помечает его загруженным
   * навсегда.
   */
  loadTreeNode(key: React.Key): Promise<void> {
    return this.modules.loading.loadChildren(String(key)).catch((error) => {
      this.expand(key, false);
      onError(error);
    });
  }

  // ---- выбор

  get selectedKeys(): React.Key[] {
    const id = this.modules.selection.selectedId;
    return id ? [id] : [];
  }

  get curNode(): CommonNodeO2 | null {
    return this.ports.curNode;
  }

  get nodeData(): ResourceState<unknown> {
    return this.modules.formData.state;
  }

  /**
   * Выбор без вопроса о несохранённых правках.
   * Для кода, который уже спросил пользователя, и для тестов.
   */
  select(key: React.Key | null): Promise<void> {
    return this.modules.nodeSelection.select(key === null ? null : String(key));
  }

  safeSelect(key: React.Key | null) {
    this.modules.nodeSelection.safeSelect(key === null ? null : String(key));
  }

  /** Повтор после ошибки загрузки объекта. */
  retry() {
    const id = this.modules.selection.selectedId;
    if (id) this.modules.nodeSelection.select(id).catch(onError);
  }

  // ---- создание и удаление

  safeCreateObject() {
    this.formStore.safeAction(() => {
      this.modules.drafts.createDraftObject().catch(onError);
    });
  }

  onNewObject(details: ZObjectItem) {
    this.ports.onNewObject(details);
  }

  /** Открыто ли окно подтверждения: сам узел разметке не нужен. */
  get isDeleting(): boolean {
    return this.modules.mutations.deletingId !== null;
  }

  get messageAboutDelete(): string {
    return this.modules.mutations.deletingMessage;
  }

  get deletingWait(): boolean {
    return this.modules.mutations.deletingWait;
  }

  cancelDelete() {
    this.modules.mutations.setDeleting(null);
  }

  doDelete(): Promise<void> {
    return this.modules.mutations.removeDeleting();
  }
}
